import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Import the enhanced logo component
const EnhancedLogo = () => {
  return (
    <motion.div 
      className="flex items-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="relative mr-3">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Glowing Background */}
          <motion.circle 
            cx="24" 
            cy="24" 
            r="20" 
            fill="rgba(212, 175, 55, 0.1)"
            animate={{ 
              scale: [1, 1.05, 1],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
          
          {/* Genie Lamp Base */}
          <path 
            d="M12 32C12 32 16 30 24 30C32 30 36 32 36 32V34C36 34 32 32 24 32C16 32 12 34 12 34V32Z" 
            fill="#D4AF37" 
          />
          
          {/* Lamp Body */}
          <path 
            d="M14 32C14 32 14 26 18 22C22 18 26 18 30 22C34 26 34 32 34 32" 
            stroke="#D4AF37" 
            strokeWidth="3" 
            strokeLinecap="round"
          />
          
          {/* Lamp Handle */}
          <path 
            d="M34 32C34 32 36 29 36 28C36 26.5 35 25 33 25" 
            stroke="#D4AF37" 
            strokeWidth="3" 
            strokeLinecap="round"
          />
          
          {/* Financial Chart Line */}
          <motion.path
            d="M18 22L22 18L26 20L30 16"
            stroke="#4285F4"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="24"
            strokeDashoffset="24"
            animate={{ strokeDashoffset: 0 }}
            transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
          />
          
          {/* Data Points */}
          <motion.circle 
            cx="18" 
            cy="22" 
            r="1.5" 
            fill="#4285F4"
            animate={{ 
              scale: [1, 1.3, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
          
          <motion.circle 
            cx="22" 
            cy="18" 
            r="1.5" 
            fill="#4285F4"
            animate={{ 
              scale: [1, 1.3, 1]
            }}
            transition={{ 
              duration: 2,
              delay: 0.5,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
          
          <motion.circle 
            cx="26" 
            cy="20" 
            r="1.5" 
            fill="#4285F4"
            animate={{ 
              scale: [1, 1.3, 1]
            }}
            transition={{ 
              duration: 2,
              delay: 1,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
          
          <motion.circle 
            cx="30" 
            cy="16" 
            r="1.5" 
            fill="#4285F4"
            animate={{ 
              scale: [1, 1.3, 1]
            }}
            transition={{ 
              duration: 2,
              delay: 1.5,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
          
          {/* Magic Sparkles */}
          <motion.path
            d="M24 12L24 14M24 8L24 10M20 12L28 12"
            stroke="#D4AF37"
            strokeWidth="1.5"
            strokeLinecap="round"
            animate={{ 
              opacity: [0, 1, 0],
              scale: [0.8, 1, 0.8]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
          
          <motion.path
            d="M32 18L34 18M28 18L30 18M32 14L32 22"
            stroke="#D4AF37"
            strokeWidth="1.5"
            strokeLinecap="round"
            animate={{ 
              opacity: [0, 1, 0],
              scale: [0.8, 1, 0.8]
            }}
            transition={{ 
              duration: 2,
              delay: 0.7,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
          
          <motion.path
            d="M16 18L18 18M12 18L14 18M16 14L16 22"
            stroke="#D4AF37"
            strokeWidth="1.5"
            strokeLinecap="round"
            animate={{ 
              opacity: [0, 1, 0],
              scale: [0.8, 1, 0.8]
            }}
            transition={{ 
              duration: 2,
              delay: 1.4,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
        </svg>
      </div>
      <div>
        <h1 className="text-2xl font-bold">
          <span className="text-midnight-navy">Research</span>
          <span className="text-pale-gold">Genie</span>
        </h1>
        <motion.div 
          className="text-xs text-electric-blue font-medium tracking-wider"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          AI-POWERED EQUITY RESEARCH
        </motion.div>
      </div>
    </motion.div>
  );
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className="bg-white shadow-md py-4 px-6 sticky top-0 z-50">
      <div className="container mx-auto flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <EnhancedLogo />
        </Link>
        
        {/* Mobile menu button */}
        <div className="md:hidden">
          <button
            onClick={toggleMenu}
            className="text-midnight-navy focus:outline-none"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
        
        {/* Desktop menu */}
        <div className="hidden md:flex space-x-8 items-center">
          <Link to="/product" className="text-midnight-navy hover:text-electric-blue transition-colors">
            Product
          </Link>
          <Link to="/agents" className="text-midnight-navy hover:text-electric-blue transition-colors">
            Agents
          </Link>
          <Link to="#" className="text-midnight-navy hover:text-electric-blue transition-colors">
            Resources
          </Link>
          <a 
            href="https://equity-research-analyst-611846019618.us-central1.run.app/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-midnight-navy hover:text-electric-blue transition-colors"
          >
            Login
          </a>
          <Link 
            to="#" 
            className="bg-electric-blue text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors"
          >
            Get Started
          </Link>
        </div>
      </div>
      
      {/* Mobile menu */}
      {isMenuOpen && (
        <motion.div 
          className="md:hidden mt-4 bg-white rounded-lg shadow-lg p-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex flex-col space-y-4">
            <Link 
              to="/product" 
              className="text-midnight-navy hover:text-electric-blue transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Product
            </Link>
            <Link 
              to="/agents" 
              className="text-midnight-navy hover:text-electric-blue transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Agents
            </Link>
            <Link 
              to="#" 
              className="text-midnight-navy hover:text-electric-blue transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Resources
            </Link>
            <a 
              href="https://equity-research-analyst-611846019618.us-central1.run.app/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-midnight-navy hover:text-electric-blue transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Login
            </a>
            <Link 
              to="#" 
              className="bg-electric-blue text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors text-center"
              onClick={() => setIsMenuOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;
